import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { environment } from '@environments/environment';
import { Permission, PermissionsCheck, User } from 'lfx-pcc';
import { BehaviorSubject, map, Observable, of, shareReplay, tap } from 'rxjs';
import { merge } from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public userID: string;
  public user: User;
  public user$: BehaviorSubject<User | null> = new BehaviorSubject<User | null>(null);
  public skipAuth0: WritableSignal<boolean> = signal(false);
  public permissions$: BehaviorSubject<Permission> = new BehaviorSubject<Permission>({});
  public permissions: Permission = {};
  public permissionsSig: WritableSignal<Permission> = signal({});
  public permissionsID: string = '';

  private userServiceAPI = `${environment.apiV2URL}/user-services`;

  private urls = {
    users: `${this.userServiceAPI}/users`,
    permissionsCheck: `${this.userServiceAPI}/me/permissions/checks`,
  };

  private http: HttpClient = inject(HttpClient);

  public constructor() {}

  public getMe(params?: HttpParams): Observable<any> {
    return this.http.get<User>(`${this.urls.users}/me`, { params }).pipe(
      shareReplay(1),
      tap((user) => {
        this.user = user;
        this.user$.next(user);
        this.userID = user.ID;
      })
    );
  }

  public getProjectPermissions(id: string, permissions?: PermissionsCheck[]): Observable<Permission> {
    const body: string[] = [];
    if (!permissions) {
      // If permissions are not passed in, use the default permissions
      if (this.permissionsID === id) {
        // If the permissions have already been fetched, return them
        return of(this.permissions);
      }

      permissions = this.getDefaultPermissions(id);
    }

    this.permissionsID = id;

    permissions.forEach((perm) => {
      if (perm.objectID && perm.objectType) {
        body.push(`${perm.resource}:${perm.action}:${perm.objectType}:${perm.objectID || id}`);
      } else {
        body.push(`${perm.resource}:${perm.action}`);
      }
    });

    return this.http.post<Permission[]>(`${this.urls.permissionsCheck}`, body).pipe(
      shareReplay(1),
      map((perms) => this.convertPermissionsToObj(perms)),
      tap((perms) => {
        this.permissions$.next(perms);
        this.permissions = perms;
        this.permissionsSig.set(perms);
      })
    );
  }

  private getDefaultPermissions(id: string): PermissionsCheck[] {
    return [
      { resource: 'v2_zoom_past_meetings', action: 'view_all', objectType: 'project', objectID: id },
      { resource: 'v2_zoom_meetings', action: 'view_all', objectType: 'project', objectID: id },
      { resource: 'v2_zoom_meetings', action: 'create', objectType: 'project', objectID: id },
      { resource: 'v2_zoom_meeting', action: 'view', objectType: 'project', objectID: id },
      { resource: 'v2_zoom_meeting', action: 'update', objectType: 'project', objectID: id },
    ];
  }
  private convertPermissionsToObj(permissions: Permission[]): Permission {
    let permsObj: Permission = {};
    for (const [key, value] of Object.entries(permissions)) {
      const keyArray = key.split(':');
      permsObj = merge(permsObj, {
        [`${keyArray[0]}`]: {
          [`${keyArray[1]}`]: value,
        },
      });
    }

    return permsObj;
  }
}
